import React from 'react'
import { motion, useScroll, useSpring } from 'framer-motion'

const GRAD = 'bg-gradient-to-r from-orange-500 via-red-500 to-amber-500'

const SECTIONS = [
  {
    title: 'Introduction',
    p: ['These terms and conditions govern your use of our website; by using our website, you accept these terms and conditions in full. If you disagree with these terms and conditions or any part of these terms and conditions, you must not use our website.'],
  },
  {
    title: 'License to use website',
    p: [
      'Unless otherwise stated, we or our licensors own the intellectual property rights in the website and material on the website. Subject to the license below, all these intellectual property rights are reserved.',
      'You may view, download for caching purposes only, and print pages from the website for your own personal use, subject to the restrictions set out below and elsewhere in these terms and conditions.',
      'You must not:',
    ],
    list: [
      'republish material from this website (including republication on another website);',
      'sell, rent or sub-license material from the website;',
      'show any material from the website in public;',
      'reproduce, duplicate, copy or otherwise exploit material on our website for a commercial purpose;',
      'edit or otherwise modify any material on the website; or',
      'redistribute material from this website except for content specifically and expressly made available for redistribution (such as our newsletter).',
    ],
  },
  {
    title: 'Acceptable use',
    p: [
      'You must not use our website in any way that causes, or may cause, damage to the website or impairment of the availability or accessibility of the website; or in any way which is unlawful, illegal, fraudulent or harmful, or in connection with any unlawful, illegal, fraudulent or harmful purpose or activity.',
      'You must not use our website to copy, store, host, transmit, send, use, publish or distribute any material which consists of (or is linked to) any spyware, computer virus, Trojan horse, worm, keystroke logger, rootkit or other malicious computer software.',
      'You must not conduct any systematic or automated data collection activities (including without limitation scraping, data mining, data extraction and data harvesting) on or in relation to our website without our express written consent.',
      'You must not use our website to transmit or send unsolicited commercial communications.',
      'You must not use our website for any purposes related to marketing without our express written consent.',
    ],
  },
  {
    title: 'Restricted access',
    p: [
      'Access to certain areas of our website is restricted. We reserve the right to restrict access to areas of our website, or indeed our whole website, at our discretion.',
      'If we provide you with a user ID and password to enable you to access restricted areas of our website or other content or services, you must ensure that that user ID and password is kept confidential. We may disable your user ID and password in our sole discretion without notice or explanation.',
    ],
  },
  {
    title: 'User generated content',
    p: [
      'In these terms and conditions, "your user content" means material (including without limitation text, images, audio material, video material and audio-visual material) that you submit to our website, for whatever purpose.',
      'You grant to us a worldwide, irrevocable, non-exclusive, royalty-free licence to use, reproduce, adapt, publish, translate and distribute your user content in any existing or future media. You also grant to us the right to sub-license these rights, and the right to bring an action for infringement of these rights.',
      "Your user content must not be illegal or unlawful, must not infringe any third party's legal rights, and must not be capable of giving rise to legal action whether against you or us or a third party (in each case under any applicable law).",
      'You must not submit any user content to the website that is or has ever been the subject of any threatened or actual legal proceedings or other similar complaint.',
    ],
  },
  {
    title: 'Limited warranties',
    p: ['Whilst we endeavor to ensure that the information on this website (excluding user content) is correct, we do not warrant its completeness or accuracy; nor do we commit to ensuring that the website remains available or that the material on the website is kept up-to-date. To the maximum extent permitted by applicable law we exclude all representations, warranties and conditions relating to this website and the use of this website.'],
  },
  {
    title: 'Limitations of liability',
    p: ['Vector Visionary will not be liable to you (whether under the law of contact, the law of torts or otherwise) in relation to the contents of, or use of, or otherwise in connection with, this website for any indirect, special or consequential loss; or for any business losses, loss of revenue, income, profits or anticipated savings, loss of contracts or business relationships, loss of reputation or goodwill, or loss or corruption of information or data.'],
  },
  {
    title: 'Exceptions',
    p: ["Nothing in this website disclaimer will exclude or limit any warranty implied by law that it would be unlawful to exclude or limit; and nothing in this website disclaimer will exclude or limit Vector Visionary's liability in respect of any death or personal injury caused by Vector Visionary's negligence; fraud or fraudulent misrepresentation on the part of Vector Visionary; or matter which it would be illegal or unlawful for Vector Visionary to exclude or limit, or to attempt or purport to exclude or limit, its liability."],
  },
  {
    title: 'Reasonableness',
    p: ['By using this website, you agree that the exclusions and limitations of liability set out in this website disclaimer are reasonable. If you do not think they are reasonable, you must not use this website.'],
  },
  {
    title: 'Other parties',
    p: ["You accept that, as a limited liability entity, Vector Visionary has an interest in limiting the personal liability of its officers and employees. You agree that you will not bring any claim personally against Vector Visionary's officers or employees in respect of any losses you suffer in connection with the website."],
  },
  {
    title: 'Unenforceable provisions',
    p: ['If any provision of this website disclaimer is, or is found to be, unenforceable under applicable law, that will not affect the enforceability of the other provisions of this website disclaimer.'],
  },
  {
    title: 'Severability',
    p: ['If a provision of these terms and conditions is determined by any court or other competent authority to be unlawful and/or unenforceable, the other provisions will continue in effect. If any unlawful and/or unenforceable provision would be lawful or enforceable if part of it were deleted, that part will be deemed to be deleted, and the rest of the provision will continue in effect.'],
  },
  {
    title: 'Exclusion of third party rights',
    p: ['These terms and conditions are for the benefit of you and us, and are not intended to benefit any third party or be enforceable by any third party. The exercise of our and your rights in relation to these terms and conditions is not subject to the consent of any third party.'],
  },
  {
    title: 'Entire agreement',
    p: ['These terms and conditions, together with our privacy policy, constitute the entire agreement between you and us in relation to your use of our website, and supersede all previous agreements in respect of your use of this website.'],
  },
]

const TermsPage = () => {
  const { scrollYProgress } = useScroll()
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30 })

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Scroll progress */}
      <motion.div style={{ scaleX: progress }} className={`fixed left-0 top-0 z-50 h-1 w-full origin-left ${GRAD}`} />

      {/* Hero */}
      <header className="relative overflow-hidden bg-slate-950 px-4 pb-32 pt-24 text-center">
        <div className="pointer-events-none absolute -left-24 top-0 h-96 w-96 rounded-full bg-orange-600/40 blur-3xl" />
        <div className="pointer-events-none absolute -right-24 bottom-0 h-96 w-96 rounded-full bg-red-500/30 blur-3xl" />
        <div className="pointer-events-none absolute left-1/2 top-10 h-72 w-72 -translate-x-1/2 rounded-full bg-amber-500/20 blur-3xl" />
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'radial-gradient(#fff 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="relative mx-auto max-w-3xl">
          <div className={`mx-auto mb-8 flex h-20 w-20 items-center justify-center rounded-3xl ${GRAD} shadow-2xl shadow-orange-500/40 ring-4 ring-white/10`}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h1 className={`${GRAD} bg-clip-text text-5xl font-black tracking-tight text-transparent md:text-7xl`}>
            Terms of Use
          </h1>
          <p className="mt-6 inline-block rounded-full bg-white/10 px-6 py-2 text-lg font-medium text-slate-200 ring-1 ring-white/20 backdrop-blur">
            We prioritize your security as much as our own
          </p>
        </motion.div>
      </header>

      <div className="relative mx-auto -mt-16 max-w-4xl space-y-8 px-4 pb-24">
        {SECTIONS.map((s, i) => (
          <motion.section
            key={s.title}
            initial={i === 0 ? { opacity: 0, y: 30 } : false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="group rounded-3xl bg-white p-8 shadow-xl shadow-orange-900/5 ring-1 ring-slate-900/5 md:p-10"
          >
            <div className="mb-6 flex items-center gap-4">
              <span className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl ${GRAD} text-xl font-black text-white shadow-lg shadow-orange-500/30`}>
                {i + 1}
              </span>
              <h2 className="text-2xl font-extrabold tracking-tight text-slate-900 transition-colors duration-300 group-hover:text-orange-600 md:text-3xl">
                {s.title}
              </h2>
            </div>
            <div className={`mb-6 h-1 w-full rounded-full ${GRAD} opacity-20`} />

            <div className="space-y-5">
              {s.p.map((t) => (
                <p key={t} className="text-[1.05rem] leading-8 text-slate-600">{t}</p>
              ))}
              {s.list && (
                <ul className="list-none space-y-3 pl-0">
                  {s.list.map((item) => (
                    <li key={item} className="flex items-start gap-3 rounded-2xl bg-orange-50/60 p-4 shadow-sm ring-1 ring-orange-100 transition duration-200 hover:-translate-y-0.5 hover:shadow-md hover:ring-orange-400">
                      <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-red-500 text-white">
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </span>
                      <span className="leading-relaxed text-slate-700">{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </motion.section>
        ))}

        <div className="border-t border-slate-200 pt-8 text-center text-sm text-slate-400">
          <p>This document was last updated on {new Date().toLocaleDateString()}</p>
        </div>
      </div>
    </div>
  )
}

export default TermsPage